import { useCallback, useEffect, useMemo, useReducer, useRef } from 'react'
import * as React from 'react'
import { FormattedMessage, useIntl } from 'react-intl'
import Heading from 'LuxKit/Typography/Heading'
import LayoutContainer from 'components/Common/LayoutContainer'
import { MAX_CAROUSEL_ITEM_COUNT } from '../../OfferCarousels/common/constants'
import { sortBy, take, uniqueBy } from 'lib/array/arrayUtils'
import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import FlightDealAirportSelect from '../FlightDealAirportSelect'
import Group from 'components/utils/Group'
import FlightDealTileSkeletonLoader from '../FlightDealSkeletonLoader'
import { OfferListEventsProvider } from 'components/OfferList/OfferListEventsContext'
import { getListName } from 'analytics/enhanced-ecommerce'
import useOfferListTracking from 'hooks/Offers/useOfferListTracking'
import FlightDealImageTile from 'components/Flights/FlightMerchandising/FlightDealImageTile'
import { REBRAND_PALETTE_CLASS } from 'lib/theme/paletteVersion'
import styled from 'styled-components'
import { rem } from 'polished'
import FullWidthCarousel from 'LuxKit/Carousel/FullWidthCarousel'
import BodyTextBlock from 'LuxKit/TextBlocks/BodyTextBlock'
import { FlightDealsCarouselStaticFilterPills } from './FlightDealsCarouselStaticFilterPills'
import Divider from 'LuxKit/Divider'
import CardCarousel from 'LuxKit/Carousel/CardCarousel'
import type { FlightsClassTypes } from 'constants/flight'
import FilterChip from 'LuxKit/Chips/FilterChip'
import FormatCurrency from 'components/Common/FormatCurrency'
import CarouselViewAllLink from 'components/Common/CarouselViewAllLink'
import TextButton from 'LuxKit/Button/TextButton'
import BodyText from 'LuxKit/Typography/BodyText'
import { mediaQueryUp } from 'components/utils/breakpoint'
import { setLogicalScrollLeft } from 'LuxKit/Carousel/utils'
import { ALL_KEY, FilterType } from '../flightDealsFilter.constants'
import { getFlightDealDestinationFilters } from '../getFlightDealDestinationFilters'

const DEFAULT_TRACKING_LIST_NAME = getListName('OfferList', 'FLIGHTS', 'Flights Best Flights')

const StyledFlightDealTileSkeletonLoader = styled(FlightDealTileSkeletonLoader)`
  width: ${rem(300)};
`

const DividerContainer = styled.div`
  height: ${rem(16)};
  padding: 0 ${rem(4)};
`

const DividerBar = styled(Divider)`
  height: ${rem(16)};
  max-height: ${rem(16)};
`

const LookingForSomethingElseContainer = styled(Group)`
  background-color: ${props => props.theme.palette.neutral.surface.lightBackground};
  padding: ${rem(12)} ${rem(20)};
  width: ${rem(300)};


  ${mediaQueryUp.tablet} {
    padding: ${rem(24)} ${rem(40)};
  }

  body.${REBRAND_PALETTE_CLASS} & {
    border-radius: var(--border-radius-m);
    overflow: hidden;
  }
`

const Content = styled(Group)`
  text-align: center;
`

type FilterState = Record<FilterType, Set<string>>

enum FilterActionType {
  TOGGLE_FILTER = 'TOGGLE_FILTER',
  SET_FILTER_VALUE = 'SET_FILTER_VALUE',
  RESET_FILTERS = 'RESET_FILTERS',
}

type FilterAction = {
  type: FilterActionType.TOGGLE_FILTER
  payload: {
    type: FilterType
    value: string
  }
} | {
  type: FilterActionType.SET_FILTER_VALUE
  payload: {
    type: FilterType
    value: Set<string>
  }
} | {
  type: FilterActionType.RESET_FILTERS
}

const initialState: FilterState = {
  destinations: new Set([ALL_KEY]),
  airlines: new Set([ALL_KEY]),
  cabins: new Set([ALL_KEY]),
}

function reducer(state: FilterState, action: FilterAction): FilterState {
  if (action.type === FilterActionType.TOGGLE_FILTER) {
    let newData = new Set([...state[action.payload.type]])

    if (action.payload.value === ALL_KEY) {
      const deleted = newData.delete(action.payload.value)
      newData = deleted ? new Set() : new Set([ALL_KEY])
    } else {
      newData.delete(ALL_KEY)
      const deleted = newData.delete(action.payload.value)
      if (!deleted) newData.add(action.payload.value)
    }

    if (newData.size === 0) {
      newData.add(ALL_KEY)
    }

    return {
      ...state,
      [action.payload.type]: newData,
    }
  }

  if (action.type === FilterActionType.SET_FILTER_VALUE) {
    return {
      ...state,
      [action.payload.type]: action.payload.value,
    }
  }

  if (action.type === FilterActionType.RESET_FILTERS) {
    return {
      ...initialState,
    }
  }

  return state
}

interface Props {
  title: React.ReactNode
  showViewAll?: boolean
  /** Overrides the default `/flights` destination of the view all link */
  viewAllHref?: string
  airports: Array<App.Airport>
  selectedAirport?: App.Airport
  defaultAirport?: App.Airport
  flightDeals: Array<App.FlightDeal>
  onAirportChange: (airport: App.Airport) => void
  loading?: boolean
  showLookingForAFlight?: boolean
  /** Analytics list that impressions and clicks are reported under */
  trackingListName?: string
}

function FlightDealsCarousel(props: Props) {
  const { title, showViewAll, viewAllHref, airports, selectedAirport, flightDeals, defaultAirport, onAirportChange, loading, showLookingForAFlight, trackingListName = DEFAULT_TRACKING_LIST_NAME } = props
  const intl = useIntl()
  const [filtersState, filtersDispatch] = useReducer(reducer, initialState)
  const carouselRef = useRef<HTMLDivElement | null>(null)
  const airport = selectedAirport ?? defaultAirport
  const showLookingForSomethingElse = showLookingForAFlight && filtersState.destinations.has(ALL_KEY)

  useEffect(() => {
    if (carouselRef.current) {
      setLogicalScrollLeft(carouselRef.current, 0)
    }
  }, [filtersState])

  const currentAirportDeals = useMemo(() => {
    if (!airport) return []

    const deals = flightDeals.filter(({ originAirportCode }) => originAirportCode === airport.code)
    return sortBy(deals, (deal) => deal.hierarchy, 'asc')
  }, [airport, flightDeals])

  useEffect(() => {
    filtersDispatch({ type: FilterActionType.RESET_FILTERS })
  }, [airport])

  const destinations = useMemo(() => getFlightDealDestinationFilters(
    currentAirportDeals,
    filtersState.airlines,
    filtersState.cabins,
    intl.locale,
    ALL_KEY,
  ), [currentAirportDeals, filtersState.airlines, filtersState.cabins, intl.locale])

  const airlines = useMemo(() => {
    const carriers = currentAirportDeals.map(deal => ({
      label: deal.carrier.name,
      value: deal.carrier.code,
      price: deal.perAdultPrice,
    }))

    const sortedCarriers = sortBy(carriers, (carrier) => carrier.price, 'asc')
    return uniqueBy(sortedCarriers, carrier => carrier.value)
  }, [currentAirportDeals])

  const cabins = useMemo(() => {
    const data = currentAirportDeals
      .filter(deal => {
        const airlineIncluded = filtersState.airlines.has(deal.carrier.code) || filtersState.airlines.has(ALL_KEY)
        const destinationIncluded = filtersState.destinations.has(deal.destinationCountry) || filtersState.destinations.has(ALL_KEY)
        return airlineIncluded && destinationIncluded
      })
      .map(deal => ({
        label: deal.fareClass,
        value: deal.fareClass as FlightsClassTypes,
        price: deal.perAdultPrice,
      }))

    const sortedCabins = sortBy(data, (data) => data.price, 'asc')
    return uniqueBy(sortedCabins, cabin => cabin.value)
  }, [currentAirportDeals, filtersState.airlines, filtersState.destinations])

  const handleToggleFilterValue = useCallback((type: FilterType, value: string) => {
    filtersDispatch({
      type: FilterActionType.TOGGLE_FILTER,
      payload: {
        type,
        value,
      },
    })

    if (type === FilterType.AIRLINES) {
      filtersDispatch({
        type: FilterActionType.SET_FILTER_VALUE,
        payload: {
          type: FilterType.CABINS,
          value: new Set([ALL_KEY]),
        },
      })

      filtersDispatch({
        type: FilterActionType.SET_FILTER_VALUE,
        payload: {
          type: FilterType.DESTINATIONS,
          value: new Set([ALL_KEY]),
        },
      })
    }

    if (type === FilterType.CABINS) {
      filtersDispatch({
        type: FilterActionType.SET_FILTER_VALUE,
        payload: {
          type: FilterType.DESTINATIONS,
          value: new Set([ALL_KEY]),
        },
      })
    }
  }, [])

  const filteredDeals = useMemo(() => {
    return currentAirportDeals.filter(deal => {
      let show = true

      if (filtersState.destinations.size && !filtersState.destinations.has(ALL_KEY)) {
        show = show && filtersState.destinations.has(deal.destinationCountry)
      }

      if (filtersState.airlines.size && !filtersState.airlines.has(ALL_KEY)) {
        show = show && filtersState.airlines.has(deal.carrier.code)
      }

      if (filtersState.cabins.size && !filtersState.cabins.has(ALL_KEY)) {
        show = show && filtersState.cabins.has(deal.fareClass)
      }

      return show
    })
  }, [currentAirportDeals, filtersState])

  const cheapestDestination = useMemo(() => {
    return sortBy(destinations, val => val.price, 'asc')[0]?.price
  }, [destinations])

  const carouselDeals = useMemo(() => take(filteredDeals, MAX_CAROUSEL_ITEM_COUNT), [filteredDeals])

  const carouselDealTiles = useMemo(() => {
    return carouselDeals.map((deal, index) => (
      <FlightDealImageTile
        key={deal.id}
        position={index}
        flightDeal={deal}
        hasMultipleAirlines
      />
    ))
  }, [carouselDeals])

  const tracking = useOfferListTracking(trackingListName)

  return (
    <VerticalSpacer gap={32}>
      <LayoutContainer>
        <VerticalSpacer gap={32}>
          <Group direction="horizontal" horizontalAlign="space-between" verticalAlign="center" gap={16}>
            <Group direction="vertical" tabletDirection="horizontal" tabletGap={8}>
              <Heading variant="heading2">{title}</Heading>
              {airport && airports.length > 1 &&
                <FlightDealAirportSelect
                  selected={airport}
                  airports={airports}
                  onChange={onAirportChange}
                />}
            </Group>

            {showViewAll && <CarouselViewAllLink to={viewAllHref ? undefined : '/flights'} href={viewAllHref}>
              {intl.formatMessage({
                id: 'edX3ed62knlw5uRLIZ-Q',
                defaultMessage: 'View all',
                description: 'Button text to view all flight deals in the flight search or booking flow',
              })}
            </CarouselViewAllLink>}
          </Group>

          {flightDeals.length > 0 && (
            <CardCarousel gap={4}>
              <FlightDealsCarouselStaticFilterPills
                airlines={airlines}
                cabins={cabins}
                selectedAirlines={filtersState.airlines}
                selectedCabins={filtersState.cabins}
                handleToggle={handleToggleFilterValue}
              />

              <Group direction="vertical" verticalAlign="center">
                <DividerContainer>
                  <DividerBar kind="primary" direction="vertical" />
                </DividerContainer>
              </Group>

              {destinations.length > 0 && <FilterChip
                size="medium"
                onClick={() => handleToggleFilterValue(FilterType.DESTINATIONS, ALL_KEY)}
                selected={filtersState.destinations.has(ALL_KEY)}
              >
                <FormattedMessage
                  id="qjF_60WLRfroIaMLwZgJ"
                  defaultMessage="<bold>All destinations</bold> {price}*"
                  description="Filter chip label showing all destinations with starting price"
                  values={{
                    bold: (chunks) => <b>{chunks}</b>,
                    price: <FormatCurrency value={cheapestDestination} />,
                  }}
                />
              </FilterChip>}

              {destinations.map(destination => (
                <FilterChip
                  key={destination.value}
                  size="medium"
                  onClick={() => handleToggleFilterValue(FilterType.DESTINATIONS, destination.value)}
                  selected={filtersState.destinations.has(destination.value)}
                >
                  <FormattedMessage
                    id="98D8KkZwcvmrJ-MKJt6k"
                    defaultMessage="<b>{label}</b> {price}*"
                    description="Flight deal destination filter chip with name, price, and asterisk"
                    values={{
                      label: destination.label,
                      price: <FormatCurrency value={destination.price} />,
                      b: (chunks: React.ReactNode) => <b>{chunks}</b>,
                    }}
                  />
                </FilterChip>
              ))}
            </CardCarousel>
          )}
        </VerticalSpacer>
      </LayoutContainer>

      <VerticalSpacer gap={8}>
        <FullWidthCarousel
          snap="start"
          itemsPerArrow={4}
          ref={carouselRef}
        >
          {loading && <>
            <StyledFlightDealTileSkeletonLoader />
            <StyledFlightDealTileSkeletonLoader />
            <StyledFlightDealTileSkeletonLoader />
          </>}

          {!loading && <OfferListEventsProvider tracking={tracking}>
            {carouselDealTiles}

            {showLookingForSomethingElse && <LookingForSomethingElseContainer
              gap={16}
              direction="vertical"
              horizontalAlign="center"
              verticalAlign="center"
            >
              <Content direction="vertical" gap={8} verticalAlign="center">
                <BodyText variant="large" weight="bold">
                  {intl.formatMessage({
                    id: 'ZlcVBOfaihVcGNfBU4XJ',
                    defaultMessage: 'Looking for a flight?',
                    description: 'Heading text for flight search prompt in the flight search or booking flow',
                  })}
                </BodyText>
                <BodyText variant="medium">
                  {intl.formatMessage({
                    id: 'k5ey1eQqGOac-5jvucD6',
                    defaultMessage: 'Search for all destinations for more amazing offers',
                    description: 'Description text encouraging users to search for all flight destinations',
                  })}
                </BodyText>
              </Content>

              <TextButton kind="secondary" size="medium" shape="square" to="/flights">
                {intl.formatMessage({
                  id: 'voMqHx-l5rNwHmDNHGzx',
                  defaultMessage: 'Search now',
                  description: 'Button text to start flight search in the flight search or booking flow',
                })}
              </TextButton>
            </LookingForSomethingElseContainer>}
          </OfferListEventsProvider>}
        </FullWidthCarousel>

        <LayoutContainer>
          <BodyTextBlock variant="small">
            {intl.formatMessage({
              id: 'huwSv3Cnp3MnNi1EClxN',
              defaultMessage: '*Starting price of the current campaigns',
              description: 'Disclaimer note about starting prices for flight deals',
            })}
          </BodyTextBlock>
        </LayoutContainer>
      </VerticalSpacer>
    </VerticalSpacer>
  )
}

export default FlightDealsCarousel
